/**
 * Site Group Settings V2 — "Smart Weather" section: wraps the legacy
 * `SiteGroupWeatherConfigurationTab` so its form internals don't have
 * to be rebuilt section-style for the V2 cutover.
 *
 * Contains:
 * - SmartWeatherSection (default export, forwardRef)
 */

import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SettingsSection } from '@sophocles/views/settings/components';
import SiteGroupWeatherConfigurationTab from '@sophocles/views/settings/components/SiteGroupWeatherConfigurationTab';

// Smart Weather section — wraps the legacy
// SiteGroupWeatherConfigurationTab (732 LOC) so the form internals
// don't have to be rebuilt section-style for the V2 cutover.  The
// tab now exposes `hideSaveButton` and `onDirtyChange` props plus
// an imperative `submit()` handle, so the unified Save bar drives
// submission instead of its own internal Save button.  Dirty
// state flows up via the parent's `__pending_weather_dirty`
// synthetic Formik field; handleSave calls
// `weatherRef.current.submit()` which awaits the embedded
// patchSiteGroupWeatherConfiguration dispatch.
//
// Visibility is feature-flag gated — backend includes
// `smart_weather` iff the WEATHER feature is effectively enabled
// for the group.
const SmartWeatherSection = React.forwardRef(function SmartWeatherSection({ siteGroupId }, ref) {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const handleDirtyChange = React.useCallback(
    isDirty => {
      setFieldValue('__pending_weather_dirty', Boolean(isDirty), false);
    },
    [setFieldValue],
  );
  return (
    <SettingsSection
      id="settings-smart-weather"
      icon={<ThunderstormIcon />}
      title={t('siteGroupSettings.v2.smartWeather', 'Smart Weather')}
      description={t(
        'siteGroupSettings.v2.smartWeatherDescription',
        'Weather-driven scheduling and notification thresholds for this group.',
      )}
    >
      <SiteGroupWeatherConfigurationTab
        ref={ref}
        siteGroupId={siteGroupId}
        hideSaveButton
        onDirtyChange={handleDirtyChange}
      />
    </SettingsSection>
  );
});

export default SmartWeatherSection;
